import { reactive } from "vue";
import { getConfig } from "@/utils/auth";
import { getSchoolinfo } from "@/utils/services";

/**
 * School profile provider.
 *
 * Centralise les informations de l'établissement connecté (déjà présentes dans
 * /api/config/ et /api/get_school_info) afin que les écrans du module Classroom
 * puissent les pré-remplir sans jamais inventer de données.
 *
 * Aucune donnée fictive n'est produite : si une information n'existe pas encore
 * dans la configuration de l'établissement, la valeur reste vide et l'écran
 * affiche un espace réservé.
 */

export const schoolProfile = reactive({
  loaded: false,
  loading: false,
  school_id: localStorage.getItem("schoolId") || "",
  name: localStorage.getItem("name_school") || localStorage.getItem("name") || "",
  logo: localStorage.getItem("school_logo") || "",
  town: localStorage.getItem("town") || "",
  region: localStorage.getItem("region") || "",
  hood: localStorage.getItem("hood") || "",
  owner: localStorage.getItem("owner") || "",
  phone: localStorage.getItem("school_phone") || "",
  email: localStorage.getItem("school_email") || "",
  po_box: localStorage.getItem("school_po_box") || "",
  registration_number: localStorage.getItem("school_registration_number") || "",
  education_system: localStorage.getItem("education_system") || "",
});

function pick(source, keys) {
  if (!source) return "";
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

function remember(key, value) {
  if (value) localStorage.setItem(key, value);
}

/** Adresse composée à partir des champs réellement renseignés. */
export function schoolAddressLine() {
  return [schoolProfile.hood, schoolProfile.town, schoolProfile.region]
    .filter((part) => part && String(part).trim() !== "")
    .join(", ");
}

/**
 * Charge le profil (config + school info). Idempotent : les écrans peuvent
 * l'appeler sans se soucier des appels concurrents.
 */
export async function loadSchoolProfile(force = false) {
  if (schoolProfile.loading) return schoolProfile;
  if (schoolProfile.loaded && !force) return schoolProfile;

  schoolProfile.loading = true;
  try {
    const config = await getConfig();
    const configRow = Array.isArray(config) ? config[0] : config;

    if (configRow) {
      schoolProfile.school_id = pick(configRow, ["school_id"]) || schoolProfile.school_id;
      schoolProfile.logo = pick(configRow, ["logo", "school_logo"]) || schoolProfile.logo;
      remember("schoolId", schoolProfile.school_id);
      remember("school_logo", schoolProfile.logo);
    }

    if (schoolProfile.school_id) {
      const response = await getSchoolinfo(schoolProfile.school_id);
      const info = Array.isArray(response?.data) ? response.data[0] : response?.data;

      if (info) {
        schoolProfile.name = pick(info, ["name", "school_name"]) || schoolProfile.name;
        schoolProfile.town = pick(info, ["town", "city"]) || schoolProfile.town;
        schoolProfile.region = pick(info, ["region"]) || schoolProfile.region;
        schoolProfile.hood = pick(info, ["hood", "address"]) || schoolProfile.hood;
        schoolProfile.owner = pick(info, ["owner", "manager"]) || schoolProfile.owner;
        schoolProfile.phone = pick(info, ["phone", "phone_number", "telephone", "contact"]);
        schoolProfile.email = pick(info, ["email", "mail"]);
        schoolProfile.po_box = pick(info, ["po_box", "bp", "postal_box"]);
        schoolProfile.registration_number = pick(info, [
          "registration_number",
          "authorization",
          "matricule",
          "code",
        ]);
        schoolProfile.education_system =
          pick(info, ["education_system"]) || schoolProfile.education_system;
        schoolProfile.logo = pick(info, ["logo", "school_logo"]) || schoolProfile.logo;

        remember("name_school", schoolProfile.name);
        remember("town", schoolProfile.town);
        remember("region", schoolProfile.region);
        remember("hood", schoolProfile.hood);
        remember("owner", schoolProfile.owner);
        remember("school_phone", schoolProfile.phone);
        remember("school_email", schoolProfile.email);
        remember("school_po_box", schoolProfile.po_box);
        remember("school_registration_number", schoolProfile.registration_number);
        remember("school_logo", schoolProfile.logo);
      }
    }

    schoolProfile.loaded = true;
  } catch (error) {
    console.error("Failed to load the school profile", error);
  } finally {
    schoolProfile.loading = false;
  }

  return schoolProfile;
}
